import { useState, useCallback, useEffect } from "react";
import { InlineMath } from "react-katex";
import { IconButton, DividerLine, PrimaryButton } from "../../../library";
import { informationBubbleStyles } from "./InformationBubbleStyles";

interface InformationBubbleProps {
  showText: boolean;
  setShowText: (show: boolean) => void;
  text?: string;
  subText?: string;
  primaryButtonText?: string;
  primaryButtonOnClick?: () => void;
}

interface TextItem {
  isEquation: boolean;
  value: string;
  isBold?: boolean;
}

const InformationBubble = ({
  showText,
  setShowText,
  text,
  subText,
  primaryButtonText,
  primaryButtonOnClick,
}: InformationBubbleProps) => {
  const [textItemList, setTextItemList] = useState<TextItem[]>([]);
  const [subTextItemList, setSubTextItemList] = useState<TextItem[]>([]);
  const [bubbleHeight, setBubbleHeight] = useState(0);
  const [bubbleWidth, setBubbleWidth] = useState(0);

  const SUB_TEXT_LENGTH_MULTIPLIER = 1.3;
  const BASE_LETTER_WIDTH = 6.66 * SUB_TEXT_LENGTH_MULTIPLIER;
  const BASE_LETTER_HEIGHT = 24;
  const MAX_TEXT_LENGTH = 60;
  const MIN_WIDTH = 180 * SUB_TEXT_LENGTH_MULTIPLIER;
  const OFFSET_WIDTH = 100;
  const OFFSET_HEIGHT = primaryButtonText && primaryButtonOnClick ? 135 : 85;

  const convertTextToTextList = useCallback(
    (text: string): TextItem[] => {
      if (!text) return [];

      const sentences = text.match(/[^.!?]+[.!?]+['"\`'"]*\s*/g) || [text];
      const result: TextItem[] = [];
      let currentChunk = "";

      for (const sentence of sentences) {
        if (
          (currentChunk + sentence).length * SUB_TEXT_LENGTH_MULTIPLIER <
          MAX_TEXT_LENGTH
        ) {
          currentChunk += sentence;
        } else {
          if (currentChunk)
            result.push({ isEquation: false, value: currentChunk });
          currentChunk = sentence;
        }
      }

      if (currentChunk) result.push({ isEquation: false, value: currentChunk });
      return result;
    },
    [SUB_TEXT_LENGTH_MULTIPLIER, MAX_TEXT_LENGTH],
  );

  useEffect(() => {
    let height = 0;
    let width = MIN_WIDTH;

    const processText = (textContent: string | undefined, setter: (items: TextItem[]) => void) => {
      if (!textContent) return;

      const items = convertTextToTextList(textContent);
      setter(items);
      height += items.length * BASE_LETTER_HEIGHT;
      width = Math.max(
        width,
        Math.min(textContent.length * BASE_LETTER_WIDTH, MIN_WIDTH),
      );
    };

    if (text) processText(text, setTextItemList);
    if (subText) processText(subText, setSubTextItemList);

    setBubbleHeight(height);
    setBubbleWidth(width);
  }, [
    text,
    subText,
    convertTextToTextList,
    BASE_LETTER_HEIGHT,
    BASE_LETTER_WIDTH,
    MIN_WIDTH,
  ]);

  if (!showText) return null;

  return (
    <div
      className="qae-root"
      style={{
        left: -(bubbleWidth + OFFSET_WIDTH),
        top: -(bubbleHeight + OFFSET_HEIGHT),
        right: OFFSET_WIDTH,
      }}
    >
      <style>{informationBubbleStyles}</style>
      <div className="qae-bubble">
        <div className="qae-card">
          <div className="qae-close">
            <div className="qae-close-row">
              <IconButton
                name="cross-red"
                size="small"
                onClick={() => setShowText(false)}
              />
            </div>
          </div>

          {textItemList.length > 0 && (
            <>
              <p className="">
                {textItemList.map((item, index) =>
                  item.isEquation ? (
                    <span className="qae-equation" key={index}>
                      <InlineMath math={item.value} />
                    </span>
                  ) : (
                    <span key={index} className="qae-text">
                      {item.value}
                    </span>
                  ),
                )}
              </p>
              <DividerLine />
            </>
          )}

          {subTextItemList.length > 0 && (
            <p className="">
              {subTextItemList.map((item, index) =>
                item.isEquation ? (
                  <span className="qae-equation" key={index}>
                    <InlineMath math={item.value} />
                  </span>
                ) : (
                  <span
                    key={index}
                    className={`${item.isBold ? "qae-subtext-bold" : ""} qae-subtext`}
                  >
                    {item.value}
                  </span>
                ),
              )}
            </p>
          )}

          {primaryButtonText && primaryButtonOnClick && (
            <div className="qae-button-row">
              <PrimaryButton
                title={primaryButtonText}
                size="small"
                onClick={primaryButtonOnClick}
              />
            </div>
          )}
        </div>

        <div
          className="qae-tail"
          style={{ rotate: "25deg" }}
        >
          <div className="qae-tail-box" />
        </div>
        <div
          className="qae-cover"
          style={{ bottom: -1, right: 44 }}
        >
          <div style={{ width: 49, height: 3 }} />
        </div>
        <div
          className="qae-cover"
          style={{ bottom: -2, right: 45 }}
        >
          <div style={{ width: 45, height: 3 }} />
        </div>
        <div
          className="qae-cover"
          style={{ bottom: -3, right: 46 }}
        >
          <div style={{ width: 41, height: 3 }} />
        </div>
        <div
          className="qae-cover"
          style={{ bottom: -4, right: 47 }}
        >
          <div style={{ width: 37, height: 3 }} />
        </div>
        <div
          className="qae-cover"
          style={{ bottom: -5, right: 48 }}
        >
          <div style={{ width: 33, height: 3 }} />
        </div>
      </div>
    </div>
  );
};

export default InformationBubble;
